import { missingRequiredFields, SELLER_FIELDS } from './targetFields';
import { autoMap, isExactFieldName } from './autoMap';
import type { ColumnMapping, ParsedFile } from './types';
import type { LearnedStore } from './autoMap';

export const EXTRACTION_EXTENSIONS = ['.pdf', '.eml'] as const;

/** Share of headers that must read as real column names for a sheet to count as a flat table. */
const FLAT_TABLE_HEADER_SHARE = 0.6;
const EXTRACTION_FILE = new RegExp(
  `(${EXTRACTION_EXTENSIONS.map((ext) => ext.replace('.', '\\.')).join('|')})$`,
  'i'
);
/** Extraction reads .xlsx but not the older .xls, which stays on the tabular path. */
const EXTRACTABLE_SPREADSHEET = /\.xlsx$/i;
const EXCEL_PLACEHOLDER_HEADER = /^column\s*\d+(_\d+)?$/i;
const FEET_HEADER = /^\d+\s*(['"\u2032\u2033\u2019\u201d;]|ft)$/i;

export function needsExtraction(fileName: string): boolean {
  return EXTRACTION_FILE.test(fileName);
}

export function canExtractSpreadsheet(fileName: string): boolean {
  return EXTRACTABLE_SPREADSHEET.test(fileName);
}

/**
 * A parsed spreadsheet stays on the free tabular path only when it already looks like the flat
 * table that path assumes: one record per row under a header row that names real columns. A matrix
 * of grades against lengths has numeric or length-like headers and very wide rows of numbers, and
 * belongs to extraction.
 */
export function looksLikeFlatTable(parsed: ParsedFile): boolean {
  if (parsed.headers.length < 2 || parsed.rows.length === 0) {
    return false;
  }

  const headerish = parsed.headers
    .map((header) => header.trim())
    .filter(
      (header) =>
        header !== '' &&
        !/^\d+([.,]\d+)?$/.test(header) &&
        !FEET_HEADER.test(header) &&
        !EXCEL_PLACEHOLDER_HEADER.test(header)
    );
  return (
    headerish.length >=
    Math.ceil(parsed.headers.length * FLAT_TABLE_HEADER_SHARE)
  );
}

/**
 * The mapping step is skipped only when nothing was guessed: the headers were confirmed before, or
 * every mapped header is the field's own name. A fuzzy match still goes through the screen, so a
 * wrong guess is seen and the confirmed mapping is remembered.
 */
export function confidentMappingOf(
  parsed: ParsedFile,
  learned: LearnedStore
): ColumnMapping | null {
  const { mapping, fromMemory } = autoMap(parsed, learned);
  const complete = missingRequiredFields(Object.values(mapping)).length === 0;
  const literal = Object.entries(mapping).every(
    ([header, key]) => key === '' || namesField(header, key)
  );
  return complete && (fromMemory || literal) ? mapping : null;
}

function namesField(header: string, key: string): boolean {
  const field = SELLER_FIELDS.find((candidate) => candidate.key === key);
  return field !== undefined && isExactFieldName(header, field);
}
